/**
 * Assign-crew picker (SCH.6).
 *
 * The explicit, discoverable way for a supervisor to put crew on a task: a
 * popover listing the roster with each member's load + conflict for the task's
 * scheduled day, multi-select, and a lead marker. Saving reconciles the whole
 * crew set (PUT /tasks/:id/assignments) and notifies the crew. Schedule-first —
 * disabled until the task has a date (the day's load can't be shown otherwise).
 * Double-booking is WARN-not-block (D-11): a member already on another task in
 * this slot can still be picked (Leo stacks jobs on one guy), or "Reassign"d to
 * move them off the other task instead.
 */
import { useMemo, useState } from 'react';
import { ArrowLeftRight, Loader2, Star, TriangleAlert, Users } from 'lucide-react';
import { toast } from 'sonner';
import type { CrewRoleCounts } from '@vari-crew/schemas';
import { useDispatchDay } from '@/api/dispatch';
import { useSetTaskAssignments } from '@/api/tasks';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';
import { isoToDay } from '@/lib/calendarGrid';
import { formatRoleDemand } from '@/lib/crewRoles';

export interface AssignCrewButtonProps {
  task: {
    id: string;
    title: string;
    scheduled_start: string | null;
    /** When provided, crew already booked on another task overlapping this
     *  window are flagged (and offered a Reassign) — still selectable (D-11). */
    scheduled_end?: string | null;
    /** The task's project — its own project-level days aren't a clash (same
     *  job). Falls back to the dispatch day's copy of the task. */
    project_id?: string;
    crew_size_needed?: number;
    /** Planned per-role crew mix (from the studio) — shown as the target demand. */
    crew_roles_needed?: CrewRoleCounts | null;
  };
}

/** Two [start,end) windows overlap? */
function windowsOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return new Date(aStart) < new Date(bEnd) && new Date(bStart) < new Date(aEnd);
}
function fmtClock(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

export function AssignCrewButton({ task }: AssignCrewButtonProps) {
  const [open, setOpen] = useState(false);
  const day = isoToDay(task.scheduled_start);

  return (
    <>
      <button
        type="button"
        disabled={!day}
        title={day ? 'Assign crew' : 'Schedule the task first to assign crew'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-accent shrink-0 disabled:opacity-40 disabled:hover:text-ink-faint"
      >
        <Users className="w-3.5 h-3.5" />
        Assign crew
      </button>
      {open && day && (
        <AssignCrewDialog task={task} day={day} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

/** Mounted only while open, so the dispatch-day fetch happens on demand. */
export function AssignCrewDialog({
  task,
  day,
  onClose,
}: {
  task: AssignCrewButtonProps['task'];
  day: string;
  onClose: () => void;
}) {
  const dispatch = useDispatchDay(day);
  const set = useSetTaskAssignments(task.id);
  const rows = dispatch.data?.crew_rows ?? [];

  const projectId = task.project_id ?? dispatch.data?.tasks_by_id?.[task.id]?.project_id;

  /**
   * This member's first other commitment overlapping this slot, if any: another
   * task, a day of another project worked directly, or a pickup. `movable` is
   * false for pickups — Reassign never vacates those (D-11).
   */
  function clashFor(
    row: (typeof rows)[number],
  ): { start: string; end: string; label: string; movable: boolean } | undefined {
    const tStart = task.scheduled_start;
    const tEnd = task.scheduled_end;
    if (!tStart || !tEnd) return undefined;
    const hits = (s: string | null, e: string | null) => !!s && !!e && windowsOverlap(s, e, tStart, tEnd);
    const a = row.assignments.find((x) => x.task_id !== task.id && hits(x.scheduled_start, x.scheduled_end));
    if (a) {
      const t = dispatch.data?.tasks_by_id?.[a.task_id];
      return { start: a.scheduled_start!, end: a.scheduled_end!, label: t?.title ?? 'another task', movable: true };
    }
    const pb = row.project_blocks.find((x) => x.project_id !== projectId && hits(x.scheduled_start, x.scheduled_end));
    if (pb) return { start: pb.scheduled_start, end: pb.scheduled_end, label: pb.project_title, movable: true };
    const pk = row.pickup_blocks.find((x) => hits(x.scheduled_start, x.scheduled_end));
    if (pk) return { start: pk.scheduled_start, end: pk.scheduled_end, label: pk.title, movable: false };
    return undefined;
  }

  // Who's on this task right now, per the day's assignments.
  const current = useMemo(() => {
    const sel = new Set<string>();
    let lead: string | null = null;
    for (const row of rows) {
      const a = row.assignments.find((x) => x.task_id === task.id);
      if (a) {
        sel.add(row.crew_member.id);
        if (a.is_lead) lead = row.crew_member.id;
      }
    }
    return { sel, lead };
  }, [rows, task.id]);

  // Local edits override the loaded state once the user touches anything.
  const [override, setOverride] = useState<{ sel: Set<string>; lead: string | null } | null>(
    null,
  );
  const view = override ?? current;

  // Crew the supervisor has explicitly chosen to *move here* off a conflicting
  // task. When the set is non-empty, save() sends reassign_conflicts so the
  // server vacates their clashing assignment instead of refusing the slot.
  const [reassigning, setReassigning] = useState<Set<string>>(new Set());

  function toggle(cid: string) {
    const base = override ?? current;
    const sel = new Set(base.sel);
    let lead = base.lead;
    if (sel.has(cid)) {
      sel.delete(cid);
      // Keep a lead designated whenever crew remain (VCS-401).
      if (lead === cid) lead = sel.size > 0 ? ([...sel][0] ?? null) : null;
      // Dropping them also cancels any pending reassign of that member.
      if (reassigning.has(cid)) {
        const next = new Set(reassigning);
        next.delete(cid);
        setReassigning(next);
      }
    } else {
      sel.add(cid);
      if (!lead) lead = cid; // first picked defaults to lead
    }
    setOverride({ sel, lead });
  }

  function makeLead(cid: string) {
    const base = override ?? current;
    if (!base.sel.has(cid)) return;
    setOverride({ sel: new Set(base.sel), lead: cid });
  }

  // Move a conflicted crew member onto this task: select them and flag the move
  // so save() asks the server to vacate their clashing assignment.
  function reassign(cid: string) {
    const base = override ?? current;
    const sel = new Set(base.sel);
    sel.add(cid);
    const lead = base.lead ?? cid;
    setOverride({ sel, lead });
    setReassigning((prev) => new Set(prev).add(cid));
  }

  function save() {
    const moveIds = [...view.sel].filter((cid) => reassigning.has(cid));
    // Picked while already booked in this slot, not moved — stacked (D-11).
    const stacked = rows
      .filter((r) => view.sel.has(r.crew_member.id) && !reassigning.has(r.crew_member.id))
      .filter((r) => !r.assignments.some((a) => a.task_id === task.id) && clashFor(r))
      .map((r) => r.crew_member.user.display_name);
    set.mutate(
      {
        assignments: [...view.sel].map((cid) => ({
          crew_member_id: cid,
          is_lead: cid === view.lead,
        })),
        ...(moveIds.length > 0
          ? { reassign_conflicts: true, reassign_crew_member_ids: moveIds }
          : {}),
      },
      {
        onSuccess: () => {
          const base =
            view.sel.size === 0
              ? 'Crew cleared'
              : moveIds.length > 0
                ? `Crew assigned (${view.sel.size}) · ${moveIds.length} reassigned`
                : `Crew assigned (${view.sel.size})`;
          if (stacked.length > 0) {
            toast.warning(`${base} — heads up: ${stacked.join(', ')} also booked during this slot`);
          } else {
            toast.success(base);
          }
          onClose();
        },
        onError: (err) => toast.error(`Assign failed: ${err.message}`),
      },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      busy={set.isPending}
      title={
        <>
          <Users className="w-4 h-4" />
          Assign crew
        </>
      }
    >
        <div className="p-4 space-y-3">
          <div>
            <div className="font-medium text-sm truncate">{task.title}</div>
            <div className="text-[11px] text-ink-faint">
              {view.sel.size} of {task.crew_size_needed ?? 1} needed · load shown for{' '}
              {new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })}
            </div>
            {formatRoleDemand(task.crew_roles_needed) && (
              <div className="text-[11px] text-ink-dim">
                Plan: {formatRoleDemand(task.crew_roles_needed)}
              </div>
            )}
          </div>

          {dispatch.isLoading ? (
            <div className="text-xs text-ink-faint py-4 text-center">Loading crew…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-ink-faint py-4 text-center">No active crew.</div>
          ) : (
            <ul className="space-y-1 max-h-72 overflow-auto">
              {rows.map((row) => {
                const cm = row.crew_member;
                const selected = view.sel.has(cm.id);
                const isLead = view.lead === cm.id;
                // Already on other work overlapping this slot? Flag it and offer
                // Reassign — but stacking them is allowed (D-11).
                const clash = clashFor(row);
                const clashing = !!clash?.movable && !selected;
                // Selected via the Reassign action — they're being moved here off
                // the clashing work (which still shows in the day's data until save).
                const movingHere = selected && reassigning.has(cm.id) && !!clash;
                const pct =
                  row.capacity_hours > 0
                    ? Math.round((row.assigned_hours / row.capacity_hours) * 100)
                    : 0;
                return (
                  <li
                    key={cm.id}
                    className={cn(
                      'flex items-center gap-2 rounded-md border px-2 py-1.5',
                      selected ? 'border-accent bg-accent/5' : 'border-line',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggle(cm.id)}
                      aria-label={`Assign ${cm.user.display_name}`}
                      title={clash ? 'Also booked during this time slot' : undefined}
                      className="shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">
                        {cm.user.display_name}
                        <span className="text-ink-faint"> · {cm.crew_role}</span>
                      </div>
                      <div className="text-[11px] flex items-center gap-1.5 min-w-0">
                        <span
                          className={cn(
                            'shrink-0',
                            pct > 100
                              ? 'text-status-blocked-fg'
                              : pct >= 80
                                ? 'text-amber-700'
                                : 'text-ink-faint',
                          )}
                        >
                          {row.assigned_hours}h / {row.capacity_hours}h ({pct}%)
                        </span>
                        {movingHere && clash ? (
                          <span className="inline-flex items-center gap-0.5 text-accent">
                            <ArrowLeftRight className="w-3 h-3" /> moving from {clash.label}
                          </span>
                        ) : clash ? (
                          <span
                            className="inline-flex items-center gap-0.5 text-amber-700 min-w-0"
                            title={`Also on ${clash.label}, ${fmtClock(clash.start)}–${fmtClock(clash.end)}`}
                          >
                            <TriangleAlert className="w-3 h-3 shrink-0" />
                            <span className="truncate">
                              also on {clash.label} {fmtClock(clash.start)}–{fmtClock(clash.end)}
                            </span>
                          </span>
                        ) : row.has_conflict ? (
                          <span className="inline-flex items-center gap-0.5 text-status-blocked-fg">
                            <TriangleAlert className="w-3 h-3" /> conflict
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {clashing ? (
                      <button
                        type="button"
                        onClick={() => reassign(cm.id)}
                        title={`Move them here off ${clash?.label ?? 'their other work'}`}
                        className="shrink-0 inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50"
                      >
                        <ArrowLeftRight className="w-3 h-3" />
                        Reassign
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={!selected}
                        onClick={() => makeLead(cm.id)}
                        title={isLead ? 'Lead' : 'Make lead'}
                        className={cn(
                          'shrink-0 inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded',
                          isLead
                            ? 'text-amber-700 font-medium'
                            : 'text-ink-faint hover:text-ink disabled:opacity-30',
                        )}
                      >
                        <Star className={cn('w-3.5 h-3.5', isLead && 'fill-amber-400')} />
                        Lead
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {[...view.sel].some((cid) => reassigning.has(cid)) && (
            <p className="text-[11px] text-amber-700 inline-flex items-center gap-1">
              <ArrowLeftRight className="w-3 h-3 shrink-0" />
              Saving moves reassigned crew off their other work.
            </p>
          )}
          {view.sel.size > 0 && !view.lead && (
            <p className="text-[11px] text-amber-700">
              Mark one crew member as the lead before saving.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} disabled={set.isPending}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={set.isPending || (view.sel.size > 0 && !view.lead)}
            >
              {set.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
    </Dialog>
  );
}
